import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  ReportProblem as ReportProblemIcon,
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon,
  Comment as CommentIcon,
} from '@mui/icons-material';
import reclamationsService, {
  RECLAMATION_REASONS,
  RECLAMATION_STATUS_LABEL,
  type CertificateReclamation,
  type ReclamationStatus,
} from '../../services/reclamationsService';

const LABS = [
  { value: 'LABORATOIRE_ICP', label: 'ICP' },
  { value: 'LABORATOIRE_SAA', label: 'SAA' },
  { value: 'LABORATOIRE_AU', label: 'AU (Fire Assay)' },
  { value: 'LABORATOIRE_VOLUMETRIE', label: 'Volumétrie' },
  { value: 'LABORATOIRE_PHYSICO_CHIMIQUE', label: 'Physico-Chimique' },
];

const STATUS_COLOR: Record<ReclamationStatus, 'warning' | 'info' | 'success' | 'error' | 'default'> = {
  OUVERTE: 'warning',
  EN_COURS: 'info',
  ACCEPTEE: 'info',
  REJETEE: 'error',
  CLOTUREE: 'success',
};

const STATUS_TABS: { label: string; value: string }[] = [
  { label: 'À traiter', value: 'OUVERTE' },
  { label: 'En cours', value: 'EN_COURS' },
  { label: 'Acceptées (réanalyse)', value: 'ACCEPTEE' },
  { label: 'Rejetées', value: 'REJETEE' },
  { label: 'Clôturées', value: 'CLOTUREE' },
  { label: 'Toutes', value: '' },
];

const reasonLabel = (v: string) =>
  RECLAMATION_REASONS.find((r) => r.value === v)?.label || v;

const ReclamationsManagement: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState<CertificateReclamation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const [resolveDialog, setResolveDialog] = useState<{
    open: boolean;
    rec: CertificateReclamation | null;
    decision: 'accept' | 'reject';
    comment: string;
    targetLab: string;
    submitting: boolean;
  }>({ open: false, rec: null, decision: 'accept', comment: '', targetLab: '', submitting: false });

  const [revisionDialog, setRevisionDialog] = useState<{
    open: boolean;
    rec: CertificateReclamation | null;
    comment: string;
    submitting: boolean;
  }>({ open: false, rec: null, comment: '', submitting: false });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const status = STATUS_TABS[tab].value;
      const list = await reclamationsService.list(status || undefined);
      setRows(list || []);
    } catch (e: any) {
      setError(e?.message || 'Erreur de chargement des réclamations');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const openResolve = (rec: CertificateReclamation, decision: 'accept' | 'reject') => {
    setResolveDialog({
      open: true,
      rec,
      decision,
      comment: '',
      targetLab: rec.target_laboratory || '',
      submitting: false,
    });
  };

  const submitResolve = async () => {
    const { rec, decision, comment, targetLab } = resolveDialog;
    if (!rec) return;
    setResolveDialog((p) => ({ ...p, submitting: true }));
    try {
      await reclamationsService.resolve(rec.id, {
        decision,
        resolution_comment: comment.trim() || undefined,
        target_laboratory: decision === 'accept' ? targetLab || undefined : undefined,
      });
      setSnack({
        msg:
          decision === 'accept'
            ? 'Réclamation acceptée : certificat révoqué et lot renvoyé à l\'analyste.'
            : 'Réclamation rejetée : le certificat reste valide.',
        sev: 'success',
      });
      setResolveDialog((p) => ({ ...p, open: false, submitting: false }));
      load();
    } catch (e: any) {
      setSnack({ msg: e?.response?.data?.message || e?.message || 'Erreur', sev: 'error' });
      setResolveDialog((p) => ({ ...p, submitting: false }));
    }
  };

  const submitRevision = async () => {
    const { rec, comment } = revisionDialog;
    if (!rec || !comment.trim()) return;
    setRevisionDialog((p) => ({ ...p, submitting: true }));
    try {
      await reclamationsService.setRevisionComment(rec.id, comment.trim());
      setSnack({ msg: 'Commentaire de révision enregistré (apparaîtra sur le certificat v2).', sev: 'success' });
      setRevisionDialog((p) => ({ ...p, open: false, submitting: false }));
      load();
    } catch (e: any) {
      setSnack({ msg: e?.response?.data?.message || e?.message || 'Erreur', sev: 'error' });
      setRevisionDialog((p) => ({ ...p, submitting: false }));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 1 }}>
        <ReportProblemIcon color="error" fontSize="large" />
        <Box>
          <Typography variant="h5" fontWeight={700}>Réclamations Certificats</Typography>
          <Typography variant="body2" color="text.secondary">
            Traitement des contestations clients : accepter (révoquer + renvoyer à l'analyste) ou rejeter.
          </Typography>
        </Box>
      </Stack>

      <Paper variant="outlined" sx={{ mt: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {STATUS_TABS.map((t) => (
            <Tab key={t.value || 'all'} label={t.label} />
          ))}
        </Tabs>
        <Divider />

        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Aucune réclamation dans cette catégorie.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Certificat</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Motif</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Détails</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Statut</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Ouverte le</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {r.certificate_number || r.certificate_id.slice(0, 8)}
                      {r.new_certificate_number && (
                        <Typography variant="caption" display="block" color="success.main">
                          → {r.new_certificate_number}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{reasonLabel(r.reason_category)}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{r.reason_text}</Typography>
                      {r.resolution_comment && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Réponse: {r.resolution_comment}
                        </Typography>
                      )}
                      {r.revision_comment && (
                        <Typography variant="caption" color="info.main" display="block">
                          Révision: {r.revision_comment}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={STATUS_COLOR[r.status]} label={RECLAMATION_STATUS_LABEL[r.status]} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{new Date(r.opened_at).toLocaleDateString('fr-FR')}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" gap={0.5} justifyContent="flex-end" flexWrap="wrap">
                        {(r.status === 'OUVERTE' || r.status === 'EN_COURS') && (
                          <>
                            <Button size="small" variant="outlined" color="success" startIcon={<AcceptIcon />} onClick={() => openResolve(r, 'accept')}>
                              Accepter
                            </Button>
                            <Button size="small" variant="outlined" color="error" startIcon={<RejectIcon />} onClick={() => openResolve(r, 'reject')}>
                              Rejeter
                            </Button>
                          </>
                        )}
                        {r.status === 'ACCEPTEE' && (
                          <Button
                            size="small"
                            color="info"
                            startIcon={<CommentIcon />}
                            onClick={() => setRevisionDialog({ open: true, rec: r, comment: r.revision_comment || '', submitting: false })}
                          >
                            Commentaire révision
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Resolve dialog */}
      <Dialog open={resolveDialog.open} onClose={() => !resolveDialog.submitting && setResolveDialog((p) => ({ ...p, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>
          {resolveDialog.decision === 'accept' ? 'Accepter la réclamation' : 'Rejeter la réclamation'}
        </DialogTitle>
        <DialogContent dividers>
          {resolveDialog.decision === 'accept' ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Le certificat <strong>{resolveDialog.rec?.certificate_number}</strong> sera révoqué (conservé en historique,
                visible côté client comme « Révoqué »). Le lot repart à l'analyste pour réimport et réanalyse, puis un
                nouveau certificat <strong>v2</strong> sera généré.
              </Alert>
              <TextField
                select
                fullWidth
                label="Renvoyer au laboratoire (analyste)"
                value={resolveDialog.targetLab}
                onChange={(e) => setResolveDialog((p) => ({ ...p, targetLab: e.target.value }))}
                helperText="Laissez vide pour détection automatique selon le lot."
                sx={{ mb: 2 }}
              >
                <MenuItem value=""><em>Détection automatique</em></MenuItem>
                {LABS.map((l) => (
                  <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
                ))}
              </TextField>
            </>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Le certificat restera valide. Indiquez au client la raison du rejet de sa réclamation.
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Commentaire pour le client"
            value={resolveDialog.comment}
            onChange={(e) => setResolveDialog((p) => ({ ...p, comment: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialog((p) => ({ ...p, open: false }))} disabled={resolveDialog.submitting}>
            Annuler
          </Button>
          <Button
            variant="contained"
            color={resolveDialog.decision === 'accept' ? 'success' : 'error'}
            onClick={submitResolve}
            disabled={resolveDialog.submitting}
          >
            {resolveDialog.submitting ? 'Traitement…' : resolveDialog.decision === 'accept' ? 'Accepter & révoquer' : 'Rejeter'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revision comment dialog */}
      <Dialog open={revisionDialog.open} onClose={() => !revisionDialog.submitting && setRevisionDialog((p) => ({ ...p, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>Commentaire de révision (certificat v2)</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ce commentaire apparaîtra sur le nouveau certificat (v2) pour indiquer qu'il a été révoqué puis réanalysé.
            Renseignez-le après réimport des nouvelles valeurs, avant la regénération du certificat.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Commentaire de révision"
            placeholder="Ex: Certificat révisé suite à réclamation client — réanalyse du lot, valeurs corrigées."
            value={revisionDialog.comment}
            onChange={(e) => setRevisionDialog((p) => ({ ...p, comment: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevisionDialog((p) => ({ ...p, open: false }))} disabled={revisionDialog.submitting}>
            Annuler
          </Button>
          <Button variant="contained" onClick={submitRevision} disabled={revisionDialog.submitting || !revisionDialog.comment.trim()}>
            {revisionDialog.submitting ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {snack && (
        <Alert
          severity={snack.sev}
          onClose={() => setSnack(null)}
          sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1400, boxShadow: 3 }}
        >
          {snack.msg}
        </Alert>
      )}
    </Box>
  );
};

export default ReclamationsManagement;
